import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Typography, Button } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import DirectionsIcon from '@mui/icons-material/Directions';
import StorefrontIcon from '@mui/icons-material/Storefront';
import MainLayout from './layout/MainLayout';
import Countdown from './home/Countdown';
import PrimaryActionCard from './home/PrimaryActionCard';
import { brand } from '../theme';
import { festivals as festivalsApi } from '../services/api';
import {
  FESTIVAL_HOURS_SHORT,
  festivalDateAtOpen,
  secondSaturdayOfNovember,
} from '../utils/festivalDates';

// Pick a sensible fallback if the active festival lookup hasn't returned yet:
// the second Saturday of November this year, or next year if it's already past.
const fallbackFestivalDate = () => {
  const now = new Date();
  const thisYear = secondSaturdayOfNovember(now.getFullYear());
  return now > thisYear
    ? secondSaturdayOfNovember(now.getFullYear() + 1)
    : thisYear;
};

const Home = () => {
  const [festival, setFestival] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Stripe 3DS redirects used to land on the site root. Send those users to
  // the confirmation page so payment_intent in the query string is handled.
  useEffect(() => {
    const paymentIntent = searchParams.get('payment_intent');
    const redirectStatus = searchParams.get('redirect_status');
    if (paymentIntent && redirectStatus !== 'failed') {
      navigate(`/vendor/confirmation?${searchParams.toString()}`, { replace: true });
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    let cancelled = false;
    festivalsApi
      .getActive()
      .then((data) => {
        if (!cancelled) setFestival(data);
      })
      .catch(() => {
        // Public-facing fallback handles the missing data path; swallow.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const festivalDate = useMemo(
    () => festivalDateAtOpen(festival?.event_date) || fallbackFestivalDate(),
    [festival?.event_date]
  );

  const eventDateLabel = festivalDate.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const registerYear = festivalDate.getFullYear();

  return (
    <MainLayout>
      {/* Hero band uses a soft cream wash so the tree and countdown sit on
          a "paper" surface, separating the editorial hero from the rest of
          the page. */}
      <Box sx={{ backgroundColor: brand.cream, borderBottom: `1px solid ${brand.hairline}` }}>
        <Container maxWidth="md" sx={{ py: { xs: 5, sm: 8 }, textAlign: 'center' }}>
          <Box
            component="img"
            src="/ginghamTree_transparent.png"
            alt="Gingham Tree"
            sx={{
              width: { xs: 220, sm: 320 },
              height: 'auto',
              mx: 'auto',
              display: 'block',
              mb: { xs: 3, sm: 4 },
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: brand.navy,
              mb: 1.5,
            }}
          >
            Gingham Tree
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
              color: brand.textSecondary,
              maxWidth: 560,
              mx: 'auto',
              mb: { xs: 4, sm: 5 },
            }}
          >
            Handmade. Local. Always the second Saturday in November.
          </Typography>

          <Typography
            sx={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: { xs: '1.125rem', sm: '1.25rem' },
              color: brand.navy,
              mb: 0.5,
              letterSpacing: '0.01em',
            }}
          >
            {eventDateLabel}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1rem', sm: '1.0625rem' },
              color: brand.textSecondary,
              mb: 2,
            }}
          >
            {FESTIVAL_HOURS_SHORT}
          </Typography>
          <Countdown targetDate={festivalDate} />
        </Container>
      </Box>

      {/* Main content shares the hero's max-width so the page reads as a
          single column at any screen size. Wider desktops keep the same
          comfortable layout instead of stretching the action cards out. */}
      <Container maxWidth="md" sx={{ py: { xs: 5, sm: 8 } }}>
        {/* Three primary actions. CSS grid keeps cards equal height and
            collapses to a single column on mobile so the buttons stack
            cleanly. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: 2.5, md: 3 },
          }}
        >
          <PrimaryActionCard
            icon={MapIcon}
            label="Festival Map"
            subtitle="See every booth"
            to="/map"
          />
          <PrimaryActionCard
            icon={DirectionsIcon}
            label="Directions"
            subtitle="Address, parking, drive time"
            to="/directions"
          />
          <PrimaryActionCard
            icon={StorefrontIcon}
            label="Vendors"
            subtitle="Browse this year's artisans"
            to="/vendors"
          />
        </Box>

        {/* Secondary information links: kept visible but quieter than the
            three primary actions above. */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 2,
            mt: { xs: 4, sm: 6 },
          }}
        >
          <Button
            component={Link}
            to="/visitors"
            variant="outlined"
            color="primary"
            size="large"
            sx={{ minWidth: 220 }}
          >
            For Visitors
          </Button>
          <Button
            component={Link}
            to="/exhibitor-info"
            variant="outlined"
            color="primary"
            size="large"
            sx={{ minWidth: 220 }}
          >
            For Exhibitors
          </Button>
        </Box>

        {/* Welcome paragraph. Trimmed from the previous block so the page
            doesn't bury the primary actions in copy. */}
        <Box sx={{ maxWidth: 640, mx: 'auto', mt: { xs: 6, sm: 9 }, textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{ color: brand.navy, mb: 2 }}
          >
            A celebration of handmade artistry
          </Typography>
          <Typography
            sx={{
              fontSize: '1.1875rem',
              lineHeight: 1.7,
              color: brand.textPrimary,
            }}
          >
            Every booth at Gingham Tree tells a story. We showcase the work of
            talented artisans and makers from across the region — handmade
            goods, hand-thrown pottery, painted canvases, stitched treasures.
            You won't find mass-produced items here, just genuine, one-of-a-kind
            creations. Every booth fee we collect goes to local education
            initiatives.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, sm: 5 } }}>
          <Button
            component={Link}
            to="/signup"
            variant="contained"
            color="primary"
            size="large"
            sx={{
              minWidth: { xs: '100%', sm: 340 },
              py: 2,
              fontSize: '1.125rem',
            }}
          >
            Register as a Vendor for {registerYear}
          </Button>
        </Box>
      </Container>
    </MainLayout>
  );
};

export default Home;
